import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PlatefullAILogo from '../../../images/PlatefullAILogoNB.svg';
import StepProgress from '../../../components/StepProgress/StepProgress';
import TermsBottomSheet from '../../../components/Legal/TermsBottomSheet';
import styles from '../Onboard.module.css';

interface AuthStepProps {
  email: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  onNext: (data: { email: string; firstName?: string; lastName?: string; password?: string }) => void;
  isLoading: boolean;
  error: string;
  isNewUser: boolean;
  toggleUserType: () => void;
  onStepClick?: (stepNumber: number) => void;
}

const AuthStep: React.FC<AuthStepProps> = ({ 
  email: initialEmail, 
  firstName: initialFirstName, 
  lastName: initialLastName, 
  password: initialPassword, 
  onNext, 
  isLoading, 
  error, 
  isNewUser, 
  toggleUserType,
  onStepClick
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail || '');
  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [password, setPassword] = useState(initialPassword || '');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  // Password validation rules
  const passwordValidation = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password)
  };
  
  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const validateEmail = (email: string): string | null => {
    if (!email) {
      return 'Email is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string | null => {
    if (confirmPassword && password && confirmPassword.length >= 8 && confirmPassword.trim() !== password.trim()) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    const error = validateEmail(value);
    setEmailError(error || '');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    const error = validatePassword(value);
    setPasswordError(error || '');
    
    // Also validate confirm password if it has a value
    if (confirmPassword) {
      const confirmError = validateConfirmPassword(confirmPassword, value);
      setConfirmPasswordError(confirmError || '');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    const error = validateConfirmPassword(value, password);
    setConfirmPasswordError(error || '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email first
    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    
    if (isNewUser) {
      const passwordValidationError = validatePassword(password);
      if (passwordValidationError) {
        setPasswordError(passwordValidationError);
        return;
      }
      
      const confirmPasswordValidationError = validateConfirmPassword(confirmPassword, password);
      if (confirmPasswordValidationError) {
        setConfirmPasswordError(confirmPasswordValidationError);
        return;
      }
      
      onNext({ email, firstName, lastName, password });
    } else {
      onNext({ email, password });
    }
  };

  const handleToggleUserType = () => {
    // Clear form errors and fields
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    
    // Toggle the user type in parent component
    toggleUserType();
  };

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.logoContainer}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
        <h2 className={styles.stepTitle}>
          {isNewUser ? 'Create Account' : 'Sign In'}
        </h2>
      </div>
      
      <StepProgress 
        currentStep={1} 
        totalSteps={4} 
        stepNames={['Account', 'Dietary', 'Health', 'Favorites']}
        onStepClick={onStepClick}
      />
      
      <div className={styles.stepDescription}>
        <div className={styles.stepDescriptionText}>
          <p>
            {isNewUser 
              ? 'Create your Digital Dietary ID to get started'
              : 'Welcome back! Sign in to your account'
            }
          </p>
        </div>
      </div>

      <div className={styles.stepContent}>
        <form onSubmit={handleSubmit} autoComplete="off" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Hidden dummy fields to confuse password managers */}
          <input type="text" style={{ display: 'none' }} />
          <input type="password" style={{ display: 'none' }} />
          
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.formLabel}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
                  className={`${styles.formInput} ${emailError ? styles.formInputError : ''}`}
              placeholder="Enter your email"
              autoComplete="username"
              autoFocus
              disabled={isLoading}
            />
            {emailError && (
              <div className={styles.errorMessage}>
                {emailError}
              </div>
            )}
          </div>

          {isNewUser && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName" className={styles.formLabel}>
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={styles.formInput}
                  placeholder="First name"
                  autoComplete="given-name"
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="lastName" className={styles.formLabel}>
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={styles.formInput}
                  placeholder="Last name"
                  autoComplete="family-name"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.formLabel}>
              Password
            </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                    className={`${styles.formInput} ${passwordError ? styles.formInputError : ''}`}
                placeholder="Enter your password"
                autoComplete={isNewUser ? 'new-password' : 'current-password'}
                autoFocus
                disabled={isLoading}
              />
              {!isNewUser && (
                <div className={styles.forgotPasswordContainer}>
                  <button
                    type="button"
                    className={styles.forgotPasswordLink}
                    onClick={() => {
                      navigate('/password-reset');
                    }}
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              
              {isNewUser && password && !isPasswordValid && (
                <div className={styles.passwordRequirements}>
                  <div className={styles.passwordRequirementsTitle}>Password must contain:</div>
                  <div className={styles.passwordRequirement}>
                    <span className={`${styles.requirementIcon} ${passwordValidation.minLength ? styles.requirementMet : ''}`}>
                      {passwordValidation.minLength ? '✓' : '○'}
                    </span>
                    At least 8 characters
                  </div>
                  <div className={styles.passwordRequirement}>
                    <span className={`${styles.requirementIcon} ${passwordValidation.hasUppercase ? styles.requirementMet : ''}`}>
                      {passwordValidation.hasUppercase ? '✓' : '○'}
                    </span>
                    One uppercase letter
                  </div>
                  <div className={styles.passwordRequirement}>
                    <span className={`${styles.requirementIcon} ${passwordValidation.hasLowercase ? styles.requirementMet : ''}`}>
                      {passwordValidation.hasLowercase ? '✓' : '○'}
                    </span>
                    One lowercase letter
                  </div>
                  <div className={styles.passwordRequirement}>
                    <span className={`${styles.requirementIcon} ${passwordValidation.hasNumber ? styles.requirementMet : ''}`}>
                      {passwordValidation.hasNumber ? '✓' : '○'}
                    </span>
                    One number
                  </div>
                </div>
              )}
          </div>

          {isNewUser && (
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword" className={styles.formLabel}>
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className={`${styles.formInput} ${confirmPasswordError ? styles.formInputError : ''}`}
                placeholder="Confirm your password"
                autoComplete="new-password"
                disabled={isLoading}
              />
              {confirmPasswordError && (
                <div className={styles.errorMessage}>
                  {confirmPasswordError}
                </div>
              )}
            </div>
          )}

          {isNewUser && (
            <div className={styles.formGroup} style={{ width: '90%' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input
                  id="acceptTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={isLoading}
                  style={{ marginTop: 4 }}
                />
                <label htmlFor="acceptTerms" className={styles.formLabel} style={{ margin: 0, fontSize: '0.85rem' }}>
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className={styles.link}
                    aria-haspopup="dialog"
                    aria-label="Open Terms & Conditions"
                    style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Terms & Conditions
                  </button>.
                </label>
              </div>
            </div>
          )}

          {(error || passwordError) && (
            <div className={styles.errorMessage}>
              {(() => {
                const errorMsg = error || passwordError;
                if (typeof errorMsg === 'string') {
                  // Try to parse JSON error messages for better display
                  try {
                    const parsed = JSON.parse(errorMsg);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                      return parsed[0].msg || errorMsg;
                    }
                    return errorMsg;
                  } catch {
                    return errorMsg;
                  }
                }
                return JSON.stringify(errorMsg);
              })()}
            </div>
          )}
        </form>
      </div>
      <TermsBottomSheet open={showTerms} onClose={() => setShowTerms(false)} />

      <div className={styles.linkContainer}>
        <button
          type="button"
          onClick={handleToggleUserType}
          className={styles.toggleLink}
          disabled={isLoading}
        >
          {isNewUser ? 'Already have an account?' : "Don't have an account?"}
        </button>
      </div>

      <div className={styles.buttonContainer}>
        <button
          type="button"
          onClick={() => onNext(isNewUser ? { email, firstName, lastName, password } : { email, password })}
          disabled={
            isLoading || 
            !email || 
            !!emailError ||
            !password || 
            !!passwordError ||
            !isPasswordValid ||
            (isNewUser && (
              !firstName || 
              !lastName || 
              !confirmPassword || 
              !!confirmPasswordError ||
                  confirmPassword !== password ||
                  !acceptedTerms
            ))
          }
          className={styles.primaryButton}
        >
          <span>
            {isLoading && <span className={styles.loadingSpinner}></span>}
            {isLoading ? 'Processing...' : (isNewUser ? 'Create Account' : 'Sign In')}
          </span>
        </button>
      </div>
    </div>
  );
};

export default AuthStep;
